/**
 * `ion-content` keeps its scroller (`.inner-scroll`) in a shadow root, so a
 * light-DOM `parentElement` walk never sees it; the host exposes it only
 * through `getScrollElement()`.
 */
type IonContentHost = HTMLElement & {
  getScrollElement: () => Promise<HTMLElement>;
};

function isIonContent(node: HTMLElement): node is IonContentHost {
  return (
    node.tagName === "ION-CONTENT" &&
    typeof (node as Partial<IonContentHost>).getScrollElement === "function"
  );
}

/**
 * The element that scrolls `el` into view: the nearest overflow scroller with
 * more to show, or an ion-content's own. A container that only clips, or one
 * that fits its content, is passed over.
 */
export async function scrollerOf(el: HTMLElement): Promise<HTMLElement | null> {
  const host = scrollHostOf(el);
  return host && isIonContent(host) ? await host.getScrollElement() : host;
}

function scrollHostOf(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    if (isIonContent(node)) {
      return node;
    }
    const { overflowY } = getComputedStyle(node);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
